import { useEffect, useState } from 'react';
import { backend } from '../data';
import type { InvitePeek, Session } from '@bhorosha/data';
import { t, tErr } from '@bhorosha/i18n';
import { AuthCard, ErrorText, Notice, Spinner } from '../ui/components';
import { AuthScreen } from './AuthScreen';

export const PENDING_INVITE_KEY = 'bh.pendingInvite';

export function inviteTokenFromUrl(): string | null {
  const m = location.pathname.match(/^\/invite\/([0-9a-f-]{36})\/?$/i);
  return m ? m[1] : null;
}

/** Shown when someone opens an invite link. */
export function InviteScreen({ token, session, onJoined, onSkip }: {
  token: string; session: Session | null; onJoined: (familyId: string) => void; onSkip: () => void;
}) {
  const [peek, setPeek] = useState<InvitePeek | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { backend.peekInvite(token).then(setPeek).catch(() => setPeek(null)); }, [token]);

  if (peek === undefined) return <Spinner />;

  const header = peek && (
    <div className="bh-invite-head">
      <h1 className="bh-h1">{t('inv.title')}</h1>
      <p>{t('inv.body', { by: peek.invitedByName || 'Someone', family: peek.familyName, role: t(`role.${peek.role}`).toLowerCase() })}</p>
    </div>
  );

  if (!peek || peek.status !== 'open') {
    return (
      <AuthCard>
        {header}
        <Notice tone="warn">{!peek ? t('inv.notFound') : t(`inv.status.${peek.status}`)}</Notice>
        <button className="bh-btn bh-btn-ghost bh-full" onClick={onSkip}>{t('common.back')}</button>
      </AuthCard>
    );
  }

  if (!session) {
    return <AuthScreen presetEmail={peek.invitedEmail} header={<>{header}<Notice>{t('inv.signInFirst', { email: peek.invitedEmail })}</Notice></>} />;
  }

  const wrongEmail = session.email.toLowerCase() !== peek.invitedEmail.toLowerCase();
  async function accept() {
    setBusy(true); setErr('');
    try { onJoined(await backend.acceptInvite(token)); }
    catch (e) { setErr(tErr(e)); }
    finally { setBusy(false); }
  }
  return (
    <AuthCard>
      {header}
      {wrongEmail
        ? <Notice tone="warn">{t('inv.wrongEmail', { me: session.email, email: peek.invitedEmail })}</Notice>
        : <p className="bh-muted">{t(`role.${peek.role}.desc`)}</p>}
      <ErrorText>{err}</ErrorText>
      {wrongEmail
        ? <button className="bh-btn bh-btn-primary bh-full" onClick={() => backend.signOut()}>{t('menu.signOut')}</button>
        : <button className="bh-btn bh-btn-primary bh-full" disabled={busy} onClick={accept}>{busy ? t('common.loading') : t('inv.accept')}</button>}
      <div className="bh-auth-foot"><button className="bh-link" onClick={onSkip}>{t('common.cancel')}</button></div>
    </AuthCard>
  );
}
